import { useBillingRepository } from '@Hooks/useRepository';
import { type MarkAsAdmonishedOptions } from '@Model';
import { InvoicesRepository } from '@Repositories/Billing';
import { isOk, useToasts } from '@synqo/frontend-component-library';
import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';

export function useSetAdmonition(invoiceIds: string[]) {
    const invoices = useBillingRepository(InvoicesRepository);
    const { pushToast, pushAllToasts, remove } = useToasts();

    const canAdmonish = invoiceIds.length !== 0;

    const { mutateAsync: markAsAdmonished, isPending } = useMutation({
        mutationFn: (options: MarkAsAdmonishedOptions) => invoices.markAsAdmonished(options),
    });

    const setAdmonitionDate = useCallback(async () => {
        if (!canAdmonish) {
            pushToast({
                title: 'Mahn-Verfahren',
                message: 'Bitte wählen Sie mindestens eine Rechnung aus.',
                variant: 'error',
            });
            return;
        }

        const id = pushToast({
            title: 'Mahn-Verfahren',
            message: 'Rechnungen werden gemahnt',
            variant: 'success',
            loading: true,
            timeout: undefined,
        });

        const options: MarkAsAdmonishedOptions = {
            invoice_ids: invoiceIds,
            admonished_at: new Date().toISOString(),
        };

        const result = await markAsAdmonished(options).finally(() => remove(id));

        if (isOk(result)) {
            const numberSuccessful = result.data;
            const message =
                numberSuccessful === 1
                    ? 'Rechnung erfolgreich gemahnt.'
                    : `${numberSuccessful} Rechnungen erfolgreich gemahnt.`;
            pushToast({
                title: 'Mahn-Verfahren',
                message: message,
                variant: 'success',
                timeout: 5_000,
            });
        } else {
            pushAllToasts(result.messages);
        }

        return isOk(result);
    }, [canAdmonish, invoiceIds, pushToast, pushAllToasts, markAsAdmonished, remove]);

    return {
        canAdmonish,
        setAdmonitionDate,
        isPending,
    };
}
